'use client';

import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore
} from 'react';

import { I_AvatarTabs } from '@/data/editProfileMock';
import { T_NotificationPreferences } from '@/ts/types/T_NotificationPreferences';
import { T_NotificationUnreadCounts } from '@/ts/types/T_NotificationUnreadCounts';
import { T_Pool } from '@/ts/types/T_Pool';
import { T_User } from '@/ts/types/T_User';

export interface I_GlobalData {
  user?: T_User | null;
  avatars?: I_AvatarTabs | null;
  pools?: T_Pool[] | null;
  isWalletConnectOpen?: boolean;
  isWelcomeOpen?: boolean;
  isAccountOpen?: boolean;
  isAuthenticating?: boolean;
  notificationPreferences?: T_NotificationPreferences | null;
  notificationUnreadCounts?: T_NotificationUnreadCounts | null;
  activePredictionTab?: 'manual' | 'aiRandomizer';
  accountSheetInitialView?: 'notifications' | null;
  isGreyListedCountry?: boolean | null;
  isCountdownRefreshing?: boolean;
}

type T_GlobalDataContext = {
  get: () => I_GlobalData;
  subscribe: (callback: () => void) => () => boolean;
  updateGlobalData: (value: Partial<I_GlobalData>) => void;
};

export const GlobalDataContext = createContext<T_GlobalDataContext>(
  {} as T_GlobalDataContext
);

export const useStoreData = (newData: I_GlobalData) => {
  //! Global data
  const _data = useRef<I_GlobalData>(newData);

  //! Subscribers
  const subscribers = useRef<Set<() => void>>(new Set());

  //! Getter
  const get = useCallback(() => _data.current, []);

  //! Update partial data
  const updateGlobalData = useCallback((value: Partial<I_GlobalData>) => {
    _data.current = { ..._data.current, ...value };
    return subscribers.current.forEach(callback => callback());
  }, []);

  //! Subscribe
  const subscribe = useCallback((callback: () => void) => {
    subscribers.current.add(callback);
    return () => subscribers.current.delete(callback);
  }, []);

  //! Sync SSR props into the store when they change (e.g. after a locale navigation
  //! re-runs the server layout with a different user session).
  //! We skip the very first render because the ref was already initialised with newData.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Only merge the SSR-seeded keys — don't overwrite transient UI state
    updateGlobalData({
      user: newData.user,
      pools: newData.pools,
      avatars: newData.avatars,
      notificationPreferences: newData.notificationPreferences,
      notificationUnreadCounts: newData.notificationUnreadCounts,
      isGreyListedCountry: newData.isGreyListedCountry
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newData.user,
    newData.pools,
    newData.avatars,
    newData.notificationPreferences,
    newData.notificationUnreadCounts,
    newData.isGreyListedCountry
  ]);

  return {
    get,
    subscribe,
    updateGlobalData
  };
};

export type T_GlobalDataProviderProps = PropsWithChildren<
  Pick<
    I_GlobalData,
    | 'user'
    | 'avatars'
    | 'pools'
    | 'notificationPreferences'
    | 'notificationUnreadCounts'
    | 'isGreyListedCountry'
  >
>;

export const GlobalDataProvider = ({
  children,
  ...props
}: T_GlobalDataProviderProps) => {
  return (
    <GlobalDataContext.Provider
      value={useStoreData({
        isCountdownRefreshing: false,
        ...props
      })}
    >
      {children}
    </GlobalDataContext.Provider>
  );
};

export const useGlobalData = <K extends keyof I_GlobalData>(
  key: K
): I_GlobalData[K] => {
  //! Global data context
  const _dataContext = useContext(GlobalDataContext);

  if (!_dataContext) {
    throw 'Error';
  }

  //! Global data
  const _data = useSyncExternalStore(
    _dataContext.subscribe,
    () => _dataContext.get()[key],
    () => _dataContext.get()[key]
  );

  return _data;
};

export const useUpdateGlobalData =
  (): T_GlobalDataContext['updateGlobalData'] => {
    //! Global data context updater
    const { updateGlobalData } = useContext(GlobalDataContext);

    return updateGlobalData;
  };
